import { selectUserInfo } from "features/user/userSlice";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { InspectionFormProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Inspection } from "assets/SideBarIcons/inspection.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import {
  InspectionFormComplete,
  InspectionFormCompleteById,
} from "subModule/src/services/inspectionform/interface";
import Loader from "layout/Loader";
import FormDialog from "common/Modal/Modal";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import DynamicSelect from "common/Select/Select";
import { useGetVehicleTypeDropdownService } from "subModule/src/services/vehicletype/useVehicleType";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

type DeleteTarget =
  | { type: "section"; sectionIndex: number }
  | { type: "item"; sectionIndex: number; itemIndex: number };

const defaultItem = {
  title: "",
  secondaryTitle: "",
  description: "",
  id: 0,
  active: true,
  coordinates: "149,75",
};

const defaultSection = {
  title: "",
  secondaryTitle: "",
  id: 0,
  active: true,
  sortOrder: 0,
  inspectionFormItems: [],
};

const actionIconButtonClass = "bg-[#E1E8E3] p-[5px] rounded-[8px]";

interface SectionItemsFieldArrayProps {
  sectionIndex: number;
  control: Control<FormData>;
  register: ReturnType<typeof useForm<FormData>>["register"];
  errors: FieldErrors<FormData>;
  primaryLanguage?: string;
  secondaryLanguage?: string;
  themeColors?: { primary?: string };
  onRequestDeleteItem: (itemIndex: number) => void;
  onFormChange: () => void;
}

function SectionItemsFieldArray({
  sectionIndex,
  control,
  register,
  errors,
  primaryLanguage,
  secondaryLanguage,
  themeColors,
  onRequestDeleteItem,
  onFormChange,
}: SectionItemsFieldArrayProps) {
  const { t } = useTranslation();
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedInspectionItemPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phInspectionItemAr") : t("phInspectionItemEn");
  const { fields, append } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.inspectionFormItems`,
  });

  const appendItem = () => {
    append({ ...defaultItem });
    onFormChange();
  };

  return (
    <>
      <Grid item xs={12}>
        <h1
          className="flex font-Saira font-[700] text-[15px] mb-0"
          style={{ color: themeColors?.primary }}
        >
          {t("inspectionFormItems")}
        </h1>
      </Grid>
      {fields.map((field, itemIndex) => (
        <Grid
          container
          item
          xs={12}
          spacing={2}
          key={field.id}
          className="ms-0 mt-0"
        >
          <Grid item xs={12} md={3}>
            <TextField
              {...register(
                `sections.${sectionIndex}.inspectionFormItems.${itemIndex}.title`,
                {
                  onChange: () => onFormChange(),
                },
              )}
              label={`${t("item")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`}
              placeholder={localizedInspectionItemPlaceholder(primaryLanguage)}
              title={`${t("item")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`
                .replace(/\s+/g, " ")
                .trim()}
              fullWidth
              error={
                !!(errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.title
              }
              helperText={
                (errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.title?.message as string
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              {...register(
                `sections.${sectionIndex}.inspectionFormItems.${itemIndex}.secondaryTitle`,
                { onChange: () => onFormChange() },
              )}
              label={`${t("item")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
              placeholder={localizedInspectionItemPlaceholder(
                secondaryLanguage,
              )}
              title={`${t("item")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                .replace(/\s+/g, " ")
                .trim()}
              fullWidth
              error={
                !!(errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.secondaryTitle
              }
              helperText={
                (errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.secondaryTitle
                  ?.message as string
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              {...register(
                `sections.${sectionIndex}.inspectionFormItems.${itemIndex}.description`,
                {
                  onChange: () => onFormChange(),
                  maxLength: {
                    value: 500,
                    message: "Description must be under 500 characters",
                  },
                },
              )}
              label={t("description")}
              placeholder={t("phInspectionItemDescription")}
              title={t("description")}
              fullWidth
              error={
                !!(errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.description
              }
              helperText={
                (errors as any)?.sections?.[sectionIndex]
                  ?.inspectionFormItems?.[itemIndex]?.description
                  ?.message as string
              }
              InputLabelProps={{ shrink: true }}
              multiline
              rows={1}
              sx={{
                "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                  width: "100%",
                  paddingRight: "0px",
                  paddingBottom: 0,
                },
                "& .MuiInputBase-inputMultiline": {
                  resize: "vertical",
                  minHeight: "29px",
                },
                "& .MuiFormHelperText-root": {
                  bottom: "-18px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2} display="flex" alignItems="end" gap={1}>
            <IconButton
              onClick={() => onRequestDeleteItem(itemIndex)}
              color="error"
              className={`${actionIconButtonClass} mt-[8px]`}
              sx={{ width: 34, height: 34 }}
            >
              <Remove sx={{ fontSize: 19 }} />
            </IconButton>
            {itemIndex === fields.length - 1 && (
              <IconButton
                onClick={appendItem}
                className={`${actionIconButtonClass} mt-[8px]`}
                sx={{ width: 34, height: 34 }}
              >
                <Add sx={{ fontSize: 19, color: themeColors?.primary }} />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
      {fields.length === 0 && (
        <Grid item xs={12}>
          <Button
            onClick={appendItem}
            className="font-Saira font-semibold italic text-[12px] border-0 p-0 bg-transparent capitalize"
            style={{ color: themeColors?.primary }}
          >
            <Add
              sx={{
                fontSize: 15,
                color: themeColors?.primary,
                marginTop: "-3px",
              }}
            />
            {t("addInspectionFormItem")}
          </Button>
        </Grid>
      )}
    </>
  );
}

export default function InspectionFormModalNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const createInitialized = useRef(false);
  const skipWatchChange = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedInspectionFormTitlePlaceholder = (lang?: string) =>
    isArabicLanguage(lang)
      ? t("phInspectionFormTitleAr")
      : t("phInspectionFormTitleEn");
  const localizedInspectionSectionPlaceholder = (lang?: string) =>
    isArabicLanguage(lang)
      ? t("phInspectionSectionAr")
      : t("phInspectionSectionEn");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      sections: [],
      active: true,
    },
  });
  const {
    fields: sectionFields,
    append: appendSection,
    remove: removeSection,
  } = useFieldArray({
    control,
    name: "sections",
  });
  const [loading, setLoading] = useState<boolean>(!!params?.id);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(!params?.id);
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "general",
    moduleLabelKey: "inspectionForms",
    moduleHref: "/inspection-forms",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: vehicleTypesData, refetch: vehicleTypesRefetch } =
    useGetVehicleTypeDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const vehicleTypes = (vehicleTypesData?.pages[0]?.data?.content || []).map(
    (item?: { id: number; title: string }) => ({
      value: item?.id,
      label: item?.title,
    }),
  );
  const accordionSx = {
    boxShadow: "none !important",
    width: "100% !important",
    padding: "7px 12px 12px 0",
    borderRadius: "8px !important",
    borderTop: "4px solid #fff",
    "&.Mui-expanded": { borderColor: themeColors?.primary },
    "& .MuiAccordionSummary-root": {
      minHeight: "unset !important",
      paddingRight: 0,
    },
  };

  const markFormChanged = () => {
    if (params?.id) setIsBtnEnabled(true);
  };

  useEffect(() => {
    vehicleTypesRefetch();
  }, []);

  useEffect(() => {
    if (params?.id && vehicleTypesData) {
      if (loading) getByID();
    }
  }, [params, vehicleTypesData]);

  useEffect(() => {
    if (params?.id) return;
    if (createInitialized.current) return;
    createInitialized.current = true;
    appendSection({
      ...defaultSection,
      inspectionFormItems: [{ ...defaultItem }],
    });
  }, [params?.id]);

  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields, params?.id]);

  useEffect(() => {
    if (!params?.id || loading) return;
    skipWatchChange.current = true;
    const subscription = watch(() => {
      if (skipWatchChange.current) {
        skipWatchChange.current = false;
        return;
      }
      setIsBtnEnabled(true);
    });
    return () => subscription.unsubscribe();
  }, [params?.id, loading, watch]);

  const getByID = () => {
    InspectionFormProfileService.getInspectionFormCompleteById(
      +(params as any)?.id,
    )
      .then((res: InspectionFormCompleteById) => {
        if (res.success) {
          const data = res?.data || {};
          reset({
            ...data,
            vehicleTypeId: data.vehicleTypeId
              ? { value: data.vehicleTypeId, label: data.vehicleTypeName }
              : null,
            sections: (data.sections || []).map((section) => ({
              ...section,
              inspectionFormItems: (section.inspectionFormItems || []).map(
                (item) => ({
                  ...item,
                  coordinates: item.coordinates || "149,75",
                }),
              ),
            })),
          });
          captureLabel(data?.title);
          setLoading(false);
          setIsBtnEnabled(false);
          skipWatchChange.current = true;
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };

  const buildPayload = (
    data: FormData,
    formId?: number,
  ): InspectionFormComplete => ({
    title: data.title,
    secondaryTitle: data.secondaryTitle,
    description: data.description,
    vehicleTypeId: data.vehicleTypeId?.value ?? null,
    vehicleTypeName: data.vehicleTypeId?.label ?? null,
    active: data.active ?? true,
    sections: (data.sections || [])
      .filter((section: any) => section.title?.trim())
      .map((section: any, sectionIndex: number) => ({
        title: section.title,
        secondaryTitle: section.secondaryTitle,
        inspectionFormId: formId || null,
        id: section.id || null,
        active: section.active ?? true,
        sortOrder: sectionIndex + 1,
        inspectionFormItems: (section.inspectionFormItems || [])
          .filter((item: any) => item.title?.trim())
          .map((item: any) => ({
            title: item.title,
            secondaryTitle: item.secondaryTitle,
            description: item.description,
            inspectionFormId: formId || null,
            id: item.id || null,
            active: item.active ?? true,
            coordinates: Array.isArray(item.coordinates)
              ? item.coordinates.toString()
              : item.coordinates || "",
            inspectionFormSectionId: section.id || null,
          })),
      })),
  });

  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "vehicleTypes")
        vehicleTypesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };

  const onSubmit = (data: FormData) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      InspectionFormProfileService.updateInspectionFormComplete(
        +params.id,
        buildPayload(data, +params.id),
      )
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("inspectionForm") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      InspectionFormProfileService.createInspectionFormComplete(
        buildPayload(data),
      )
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("inspectionForm") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate("/inspection-forms");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const requestDeleteSection = (sectionIndex: number) => {
    setDeleteTarget({ type: "section", sectionIndex });
    setOpenDeleteModal(true);
  };

  const requestDeleteItem = (sectionIndex: number, itemIndex: number) => {
    setDeleteTarget({ type: "item", sectionIndex, itemIndex });
    setOpenDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "section") {
      removeSection(deleteTarget.sectionIndex);
    } else {
      const itemsPath = `sections.${deleteTarget.sectionIndex}.inspectionFormItems`;
      const items = getValues(itemsPath) || [];
      setValue(
        itemsPath,
        items.filter((_: any, idx: number) => idx !== deleteTarget.itemIndex),
      );
    }
    setOpenDeleteModal(false);
    setDeleteTarget(null);
    markFormChanged();
  };

  const deleteModalTitle =
    deleteTarget?.type === "section"
      ? `${t("delete")} ${t("InspectionSection")}`
      : `${t("delete")} ${t("inspectionFormItem")}`;

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate("/inspection-forms")}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("inspectionForms") : t("addInspectionForm")}
        icon={<Inspection height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_INSPECTIONFORM_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? t("inspectionForms") : t("addInspectionForm")}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              className="items-baseline mb-2"
              spacing={2}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("title", {
                    validate: {
                      required: (value) =>
                        value?.trim() !== "" || t("This field is required."),
                    },
                    onChange: () => markFormChanged(),
                  })}
                  label={`${t("Form")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                  placeholder={localizedInspectionFormTitlePlaceholder(
                    primaryLanguage,
                  )}
                  title={`${t("Form")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("secondaryTitle", {
                    onChange: () => markFormChanged(),
                  })}
                  label={`${t("Form")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                  placeholder={localizedInspectionFormTitlePlaceholder(
                    secondaryLanguage,
                  )}
                  title={`${t("Form")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.secondaryTitle}
                  helperText={errors.secondaryTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("description", {
                    onChange: () => markFormChanged(),
                  })}
                  label={t("description")}
                  placeholder={t("phInspectionFormDescription")}
                  title={t("description")}
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message as string}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  rows={1}
                  sx={{
                    "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                      width: "100%",
                      paddingRight: "0px",
                      paddingBottom: 0,
                    },
                    "& .MuiInputBase-inputMultiline": {
                      resize: "vertical",
                      minHeight: "29px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="vehicleTypeId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("vehicleType") + " *"}
                      defaultPlaceholder="phSelectVehicleType"
                      {...field}
                      options={vehicleTypes}
                      isSearchable
                      isLoading={searchLoading === "vehicleTypes"}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        markFormChanged();
                      }}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "vehicleTypes",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || pageNo > 1)
                            handleDropdownActions(
                              "vehicleTypes",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(vehicleTypesData?.pages[0]?.data as any).last)
                          handleDropdownActions("vehicleTypes", "loadNext");
                      }}
                      error={errors.vehicleTypeId?.message as string}
                      page={pageNo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box className="flex justify-start">
                  <h1
                    className="flex font-Saira font-[700] text-[17px] mb-0 text-center"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("inspectionSections")}
                  </h1>
                </Box>
              </Grid>

              {sectionFields.map((sectionField, sectionIndex) => {
                const sectionTitle =
                  watch(`sections.${sectionIndex}.title`) ||
                  `${t("InspectionSection")} ${sectionIndex + 1}`;
                return (
                  <Grid
                    item
                    container
                    xs={12}
                    spacing={1}
                    key={sectionField.id}
                    className="mt-0"
                  >
                    <Accordion
                      defaultExpanded={
                        sectionIndex === 0 ||
                        sectionIndex === sectionFields.length - 1
                      }
                      sx={accordionSx}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            className={actionIconButtonClass}
                            sx={{
                              width: 34,
                              height: 34,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{ fontSize: 19, color: themeColors?.primary }}
                            />
                          </Box>
                        }
                        aria-controls={`section-content-${sectionIndex}`}
                        id={`section-header-${sectionIndex}`}
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            margin: "0 !important",
                          },
                        }}
                      >
                        <Box className="flex items-center justify-between w-full pe-2">
                          <h1
                            className="flex font-Saira font-[600] text-[14px] mb-0"
                            style={{ color: themeColors?.primary }}
                          >
                            {sectionTitle}
                          </h1>
                          {sectionFields.length > 0 && (
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                requestDeleteSection(sectionIndex);
                              }}
                              color="error"
                              className={actionIconButtonClass}
                              sx={{ width: 34, height: 34 }}
                            >
                              <Remove sx={{ fontSize: 19 }} />
                            </IconButton>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register(`sections.${sectionIndex}.title`, {
                                onChange: () => markFormChanged(),
                              })}
                              label={`${t("section")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`}
                              placeholder={localizedInspectionSectionPlaceholder(
                                primaryLanguage,
                              )}
                              title={`${t("section")} ${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`
                                .replace(/\s+/g, " ")
                                .trim()}
                              fullWidth
                              error={
                                !!(errors as any)?.sections?.[sectionIndex]
                                  ?.title
                              }
                              helperText={
                                (errors as any)?.sections?.[sectionIndex]?.title
                                  ?.message as string
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register(
                                `sections.${sectionIndex}.secondaryTitle`,
                                {
                                  onChange: () => markFormChanged(),
                                },
                              )}
                              label={`${t("section")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                              placeholder={localizedInspectionSectionPlaceholder(
                                secondaryLanguage,
                              )}
                              title={`${t("section")} ${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                                .replace(/\s+/g, " ")
                                .trim()}
                              fullWidth
                              error={
                                !!(errors as any)?.sections?.[sectionIndex]
                                  ?.secondaryTitle
                              }
                              helperText={
                                (errors as any)?.sections?.[sectionIndex]
                                  ?.secondaryTitle?.message as string
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={1} sx={{ marginTop: "10px" }}>
                          <SectionItemsFieldArray
                            sectionIndex={sectionIndex}
                            control={control}
                            register={register}
                            errors={errors}
                            primaryLanguage={primaryLanguage}
                            secondaryLanguage={secondaryLanguage}
                            themeColors={themeColors}
                            onRequestDeleteItem={(itemIndex) =>
                              requestDeleteItem(sectionIndex, itemIndex)
                            }
                            onFormChange={markFormChanged}
                          />
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })}

              <Grid item xs={12} className="flex justify-center">
                <Button
                  onClick={() => {
                    appendSection({
                      ...defaultSection,
                      inspectionFormItems: [{ ...defaultItem }],
                    });
                    markFormChanged();
                  }}
                  className="font-Saira font-semibold italic text-[12px] border-0 p-0 bg-transparent capitalize flex gap-1"
                  style={{ color: themeColors?.primary }}
                >
                  <Add
                    sx={{
                      fontSize: 15,
                      color: themeColors?.primary,
                      marginTop: "-3px",
                    }}
                  />
                  {t("addInspectionSection")}
                </Button>
              </Grid>
            </Grid>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => {
            setOpenDeleteModal(false);
            setDeleteTarget(null);
          }}
          title={deleteModalTitle}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={confirmDelete}
          submitButtonText="Delete"
        />
      )}
    </>
  );
}
